import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Upload, Trash2, Loader2, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const LeadDocumentsPanel = ({ leadId }) => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const token = encodeURIComponent(localStorage.getItem('token') || '');

  const load = useCallback(() => {
    axios.get(`${API_URL}/leads/${leadId}/documents`).then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: demasiado grande (máx 10MB)`); continue; }
      const fd = new FormData();
      fd.append('file', f);
      try { await axios.post(`${API_URL}/leads/${leadId}/documents`, fd); ok += 1; } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || 'erro no upload'}`);
      }
    }
    if (ok) toast.success(`${ok} documento(s) carregado(s)`);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Apagar "${d.filename}"?`)) return;
    try { await axios.delete(`${API_URL}/leads/${leadId}/documents/${d.id}`); load(); } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao apagar');
    }
  };

  return (
    <Card className="rounded-2xl border-slate-100" data-testid="lead-documents-panel">
      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold flex items-center gap-2 whitespace-nowrap"><FileText className="w-4 h-4" />Faturas / Documentos</CardTitle>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="gap-1" data-testid="lead-doc-upload-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}Carregar
        </Button>
        <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden"
          onChange={(e) => upload([...e.target.files])} data-testid="lead-doc-input" />
      </CardHeader>
      <CardContent className="p-4 space-y-2">
        {docs.length === 0 && <p className="text-xs text-slate-400" data-testid="lead-docs-empty">Sem documentos. Carregue a fatura do cliente (PDF ou foto, máx. 10MB).</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2" data-testid={`lead-doc-${d.id}`}>
            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <a href={`${API_URL}/files/${d.id}/download?auth=${token}`} target="_blank" rel="noreferrer" className="text-sm text-[#2D3E6F] hover:underline truncate block" data-testid={`lead-doc-open-${d.id}`}>{d.filename}</a>
              <p className="text-[11px] text-slate-500">{fmtSize(d.size)} · {d.uploaded_by_name}{d.contract_id ? ' · no contrato' : ''}</p>
            </div>
            <a href={`${API_URL}/files/${d.id}/download?auth=${token}`} download className="text-slate-400 hover:text-slate-700"><Download className="w-4 h-4" /></a>
            {!d.contract_id && (
              <button type="button" onClick={() => remove(d)} className="text-slate-400 hover:text-rose-600" data-testid={`lead-doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
